import { useEffect, useState } from 'react'

import {
  FetchSidebarFavoritesDocument,
  MarkResourceAsFavoriteDocument,
  MarkResourceAsFavoriteMutationVariables,
  RemoveResourceFromFavoritesDocument,
  UpdateFavoriteResourceDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

import {
  type Favorite,
  useSidebarFavorites,
} from 'modules/layout/components/RootLayout/FactorialOneLayout/SidebarFavoritesProvider'
import { FAVORITE_EVENTS } from 'modules/layout/lib/tracking'

import { ResourceHandleData } from './useResolvedHandles'

type Favorites = {
  isMarked: boolean
  mutating: boolean
  label: string
  onChange: (isAdding: boolean) => void
}

export const useFavorites = (
  resource: ResourceHandleData | undefined,
  moduleId = ''
): Favorites | undefined => {
  const { idByUrl: favoriteIdByUrl, raw: favorites } = useSidebarFavorites()
  const [failedUpdates, setFailedUpdates] = useState<Favorite['id'][]>([])
  const [markAsFavorite, { loading: isMarking }] = useMarkAsFavorite()
  const [removeFromFavorites, { loading: isRemoving }] = useRemoveFromFavorites()

  const [updateFavorite, { loading: isUpdating }] = useMutation(UpdateFavoriteResourceDocument, {
    extractorFn: (data) => data.layoutMutations.updateLayoutFavourite,
    update: (cache, { data }) => {
      cache.updateQuery({ query: FetchSidebarFavoritesDocument }, (cachedData) => {
        const sidebarFavorites = cachedData?.layout.favouritesConnection.nodes ?? []
        const updatedFavorite = data?.layoutMutations.updateLayoutFavourite.favourite

        if (updatedFavorite) {
          const updatedIndex = sidebarFavorites.findIndex((fav) => fav.id === updatedFavorite.id)
          if (updatedIndex > -1) {
            const newSidebarFavorites = [...sidebarFavorites]
            newSidebarFavorites[updatedIndex] = updatedFavorite

            return {
              ...cachedData,
              layout: {
                favouritesConnection: {
                  nodes: newSidebarFavorites,
                },
              },
            }
          }
        }

        return cachedData
      })
    },
    optimisticResponse: (vars) => ({
      layoutMutations: {
        updateLayoutFavourite: {
          errors: [],
          favourite: {
            avatar: null,
            avatarType: null,
            description: null,
            position: -1,
            ...vars,
          },
        },
      },
    }),
  })

  useEffect(() => {
    if (resource && resource.path in favoriteIdByUrl) {
      const favoriteId = favoriteIdByUrl[resource.path]
      const favorite = favorites.find((fav) => fav.id === favoriteId)

      if (
        favorite &&
        !failedUpdates.includes(favorite.id) &&
        hasFavoriteChanged(favorite, resource)
      ) {
        updateFavorite({
          variables: {
            ...buildFavoriteVariables(resource, moduleId),
            id: favorite.id,
          },
          onError: () => {
            setFailedUpdates((prev) => [favorite.id, ...prev])
          },
        })
      }
    }
  }, [resource, favoriteIdByUrl, favorites, updateFavorite, moduleId, failedUpdates])

  if (!resource) {
    return undefined
  }

  const isMarked = resource.path in favoriteIdByUrl
  return {
    isMarked,
    mutating: isMarking || isRemoving || isUpdating,
    label: isMarked ? i18n.t('common.remove_favorite') : i18n.t('common.mark_favorite'),
    onChange: (isAdding: boolean) => {
      const favoriteVariables = buildFavoriteVariables(resource, moduleId)
      track(isAdding ? FAVORITE_EVENTS.add : FAVORITE_EVENTS.remove, {
        url: favoriteVariables.url,
        label: favoriteVariables.label,
      })

      if (isAdding) {
        markAsFavorite({ variables: favoriteVariables })
      } else {
        removeFromFavorites({ variables: { id: favoriteIdByUrl[resource.path] ?? 0 } })
      }
    },
  }
}

const useMarkAsFavorite = () => {
  return useMutation(MarkResourceAsFavoriteDocument, {
    extractorFn: (data) => data.layoutMutations.createLayoutFavourite,
    refetchQueries: [FetchSidebarFavoritesDocument],
    update: (cache, { data }) => {
      // manually include the new favorite to the graphql cache to keep the interaction snappy
      cache.updateQuery({ query: FetchSidebarFavoritesDocument }, (cachedData) => {
        const sidebarFavorites = cachedData?.layout.favouritesConnection.nodes ?? []
        const addedFavorite = data?.layoutMutations.createLayoutFavourite.favourite

        if (addedFavorite) {
          return {
            ...cachedData,
            layout: {
              favouritesConnection: {
                nodes: [...sidebarFavorites, { ...addedFavorite }],
              },
            },
          }
        }

        return cachedData
      })
    },
    optimisticResponse: (vars) => ({
      layoutMutations: {
        createLayoutFavourite: {
          errors: [],
          favourite: {
            avatar: null,
            avatarType: null,
            description: null,
            id: 0,
            position: -1,
            ...vars,
          },
        },
      },
    }),
  })
}

export const useRemoveFromFavorites = () => {
  return useMutation(RemoveResourceFromFavoritesDocument, {
    extractorFn: (data) => data.layoutMutations.deleteLayoutFavourite,
    refetchQueries: [FetchSidebarFavoritesDocument],
    update: (cache, { data }) => {
      // manually remove the favorite from graphql cache to keep the interaction snappy
      cache.updateQuery({ query: FetchSidebarFavoritesDocument }, (cachedData) => {
        const sidebarFavorites = cachedData?.layout.favouritesConnection.nodes ?? []
        const removedFavorite = data?.layoutMutations.deleteLayoutFavourite.favourite

        if (removedFavorite) {
          const removedIndex = sidebarFavorites.findIndex((fav) => fav.id === removedFavorite.id)
          if (removedIndex > -1) {
            const newSidebarFavorites = [...sidebarFavorites]
            newSidebarFavorites.splice(removedIndex, 1)

            return {
              ...cachedData,
              layout: {
                favouritesConnection: {
                  nodes: newSidebarFavorites,
                },
              },
            }
          }
        }

        return cachedData
      })
    },
    optimisticResponse: (vars) => ({
      layoutMutations: {
        deleteLayoutFavourite: {
          errors: [],
          favourite: {
            ...vars,
            label: '',
            url: '',
            icon: '',
            description: null,
            avatar: null,
            avatarType: null,
            position: -1,
          },
        },
      },
    }),
  })
}

const hasFavoriteChanged = (current: Favorite, resourceInfo: ResourceHandleData): boolean => {
  if (!resourceInfo?.meta?.headerComponentProps) {
    return !!resourceInfo.meta?.title && current.label !== resourceInfo?.meta?.title
  }

  const avatar = resourceInfo.meta.headerComponentProps?.avatar
  const avatarUrl = avatar && 'src' in avatar ? (avatar.src ?? null) : null
  const title = resourceInfo.meta.headerComponentProps.title
  const description = resourceInfo.meta.headerComponentProps.description ?? null

  return (
    avatarUrl !== current.avatar || title !== current.label || description !== current.description
  )
}

const buildFavoriteVariables = (
  resourceInfo: ResourceHandleData,
  moduleId = ''
): MarkResourceAsFavoriteMutationVariables => {
  const baseVariables = {
    url: resourceInfo.path,
    icon: moduleId,
  }

  if (!resourceInfo?.meta?.headerComponentProps) {
    return {
      ...baseVariables,
      label: resourceInfo?.meta?.title ?? '',
    }
  }

  const avatar = resourceInfo.meta.headerComponentProps?.avatar
  return {
    ...baseVariables,
    avatar: avatar && 'src' in avatar ? (avatar.src ?? null) : null,
    avatarType: avatar?.type,
    description: resourceInfo.meta.headerComponentProps.description ?? null,
    label: resourceInfo.meta.headerComponentProps.title,
  }
}
